import Router from "next/router";
import { useMemo, useState } from "react";
import { useMutation, useQueryClient } from "react-query";

import { bookEventById } from "~/api/events/bookEventById";
import { cancelEventById } from "~/api/events/cancelEventById";
import { inviteToEventById } from "~/api/events/inviteToEventById";
import { uninviteFromEventById } from "~/api/events/uninviteFromEventById";
import Img from "~/components/common/img";
import MainButton from "~/components/common/mainButton";
import Modal from "~/components/ui/modal";
import { useAuth } from "~/contexts/providers/auth";
import { useEvent } from "~/contexts/providers/events";
import { useLanguage } from "~/contexts/providers/language";
import englishTranslations from "~/localization/en";
import frenchTranslations from "~/localization/fr";
import { EventType, GuestType } from "~/types/event/event.types";
import { BookingStatus, EventTypeEnum } from "~/types/event/eventEnums.type";
import { locationToColors, locationToNames } from "~/utils/constants/locations";
import { formatDate, formatTime } from "~/utils/functions/date.functions";
import { getLanguage } from "~/utils/functions/language.function";
import {
  capitalizeFirstLetter,
  formatNumber,
} from "~/utils/functions/string.functions";

import CancelEventAttendance from "../cancelEvent";
import GuestButton from "../guestButton";
import GuestForm from "../guestForm";
import styles from "./index.module.scss";

type Props = {
  event: EventType;
  readonly?: boolean;
  setCannotBookPopup: (value: boolean) => void;
};

const Event = ({ event, readonly = true, setCannotBookPopup }: Props) => {
  const [modalOpen, setModalOpen] = useState(false);

  const { setMyEvents, myEvents } = useEvent();
  const { user } = useAuth();

  const queryClient = useQueryClient();
  const { language } = useLanguage();
  const accountTexts =
    language === "En"
      ? englishTranslations.accountPage
      : frenchTranslations.accountPage;

  // API Hooks
  const { mutate: BookEventById, isLoading: bookIsLoading } = useMutation(
    bookEventById,
    {
      onSuccess: (res) => {
        queryClient.invalidateQueries("events");
        setMyEvents((prev) => [...prev, { ...event, booking: res }]);
      },
    }
  );
  const { mutate: CancelEventById, isLoading: cancelIsLoading } = useMutation(
    cancelEventById,
    {
      onSuccess: () => {
        queryClient.invalidateQueries("events");
        setMyEvents((prev) => prev.filter((e) => e.id !== event.id));
      },
    }
  );

  const { mutate: InviteToEventById, isLoading: inviteIsLoading } = useMutation(
    inviteToEventById,
    {
      onSuccess: (res) => {
        queryClient.invalidateQueries("events");
        setMyEvents((prev) => {
          return [
            ...prev.filter((e) => e.id !== event.id),
            { ...event, booking: res },
          ];
        });
      },
    }
  );

  const { mutate: UninviteFromEventById, isLoading: uninviteIsLoading } =
    useMutation(uninviteFromEventById, {
      onSuccess: (res) => {
        queryClient.invalidateQueries("events");
        setMyEvents((prev) => {
          return [
            ...prev.filter((e) => e.id !== event.id),
            { ...event, booking: res },
          ];
        });
      },
    });

  const handleSwitchModalState = (statut: boolean) => {
    setModalOpen(statut);
  };

  const handleBookEvent = (guests: GuestType[]) => {
    BookEventById({ eventId: event.id, guests });
    handleSwitchModalState(false);
  };

  const handleCancelEvent = () => {
    CancelEventById({ eventId: event.id });
  };

  const handleInviteToEvent = (guests: GuestType[]) => {
    InviteToEventById({ eventId: event.id, guests });
    handleSwitchModalState(false);
  };

  const [eventDate, timeFrom, timeTo] = useMemo(
    () => [
      capitalizeFirstLetter(formatDate(event.dateFrom, language).split(".")[0]),
      formatTime(event.dateFrom),
      formatTime(event.dateTo),
    ],
    [event.dateFrom, event.dateTo, language]
  );

  const canBook = useMemo(
    () =>
      myEvents.filter(({ dateFrom }) => new Date(dateFrom) > new Date())
        .length < 5,
    [myEvents]
  );

  const handleBookClick = () => {
    if (event.type === EventTypeEnum.NORMAL) {
      if (!canBook) {
        setCannotBookPopup(true);
        console.log("you can't book");
        return;
      }
      handleSwitchModalState(true);
    } else if (event.bookingLink) Router.push(event.bookingLink);
  };

  const guestsList = useMemo(() => {
    if (event.booking?.guests.length) {
      return event.booking?.guests;
    }
    return [];
  }, [event.booking?.guests]);

  const allowedGuests =
    user?.subscriptionType === "MEMBER_PLUS"
      ? event.allowedGuestsPerParticipant * 2
      : event.allowedGuestsPerParticipant;
  const bookingGuests = event.booking?.guests.length ?? 0;

  const guestNumber = event.booking ? bookingGuests : allowedGuests;

  const remainingGuests = allowedGuests - bookingGuests;

  const subtitleAnimationStyles = useMemo(() => {
    const eventSubtitlesLength = event[`subtitle${language}`]?.length ?? 0;
    return eventSubtitlesLength > 100
      ? {
          animationDuration: `${eventSubtitlesLength * 100}ms`,
          textIndent: 0,
          paddingLeft: "100%",
        }
      : undefined;
  }, [event[`subtitle${language}`]]);

  const eventTitle =
    language == "En" ? event.titleEn ?? event.title : event.title;

  return (
    <div className={styles.container}>
      <div className={styles.eventInfo}>
        <div className={styles.eventInfoLine}>
          <div className={styles.date}>{eventDate}</div>
          <div
            className={styles.eventLocation}
            style={{ color: locationToColors[event.location] }}
          >
            {locationToNames[event.location]}
          </div>
        </div>

        <div className={styles.eventInfoLine}>
          <div className={styles.time}>
            {timeFrom} → {timeTo}
          </div>
          <div className={styles.eventTags}>
            {event.tags
              .map((tag) => getLanguage(tag, "name", language))
              .join(", ")}
          </div>
        </div>

        <div className={styles.eventInfoLine}>
          <div className={styles.buttonsContainer}>
            {!event.booking ? (
              <MainButton
                theme={"white"}
                onClick={handleBookClick}
                isLoading={bookIsLoading}
              >
                {event.isFull ? accountTexts.waitingList : accountTexts.book}
              </MainButton>
            ) : (
              <MainButton
                theme={
                  event.booking.status === BookingStatus.MAIN
                    ? "green"
                    : "yellow"
                }
                isLoading={cancelIsLoading}
                uiOnly
              >
                {event.booking.status === BookingStatus.MAIN
                  ? accountTexts.attending
                  : accountTexts.waitlist}
              </MainButton>
            )}

            {event.booking && (
              <CancelEventAttendance
                handleCancelEvent={handleCancelEvent}
                cancelIsLoading={cancelIsLoading}
              />
            )}
            {(canBook || event.booking?.status) &&
              event.type === EventTypeEnum.NORMAL && (
                <div className={styles.guestsButtonContainer}>
                  {event.booking
                    ? guestNumber === 0
                      ? ""
                      : guestNumber > 1
                      ? accountTexts.invitedGuests
                      : accountTexts.invitedGuest
                    : `${accountTexts.invitation}${guestNumber > 1 ? "s" : ""}`}
                  &nbsp;&nbsp;
                  <GuestButton
                    isLoading={
                      inviteIsLoading || uninviteIsLoading || bookIsLoading
                    }
                    guestNumber={guestNumber}
                    hasBooking={!!event.booking}
                    guests={event.booking?.guests}
                  />
                </div>
              )}
          </div>
        </div>
      </div>

      <div className={styles.imageContainer}>
        <Img
          src={event.picturePath}
          alt={eventTitle}
          containerProps={{ className: styles.imageContainer }}
        >
          <div className={styles.eventNameContainer}>
            <MainButton className={styles.eventName} uiOnly>
              {eventTitle}
            </MainButton>
            {event[`subtitle${language}`] && (
              <div className={styles.eventSubtitle}>
                <span style={subtitleAnimationStyles}>
                  <small>{event[`subtitle${language}`]}</small>
                </span>
              </div>
            )}
          </div>
          <div className={`${styles.description} no-scrollbar`}>
            {event[`description${language}`]}
          </div>
        </Img>
      </div>

      {!readonly && (
        <div className={styles.invitedGuestsContainer}>
          <div className="textCenter">
            {guestsList.length <= 1
              ? accountTexts.invitedGuest
              : accountTexts.invitedGuests}
          </div>
          {guestsList.map((guest) => {
            return (
              <div key={guest.id} className={styles.invitedGuestsLine}>
                <div
                  className={styles.guestName}
                >{`${guest.firstname} ${guest.lastname}`}</div>
                <button
                  onClick={() =>
                    UninviteFromEventById({
                      eventId: event.id,
                      id: guest.id,
                    })
                  }
                  className={styles.removeGuest}
                >
                  Supprimer
                </button>
              </div>
            );
          })}
          {remainingGuests > 0 && (
            <MainButton
              theme="transparent-grey"
              onClick={() => handleSwitchModalState(true)}
              isLoading={inviteIsLoading}
            >
              {`Ajouter Invité ${formatNumber(remainingGuests)}`}
            </MainButton>
          )}
        </div>
      )}
      <Modal
        open={modalOpen}
        onClose={() => handleSwitchModalState(false)}
        showAnimation="fade-in"
      >
        {!event.booking ? (
          <GuestForm
            type="book"
            eventName={eventTitle}
            currentGuests={0}
            allowedGuests={remainingGuests}
            handleSubmitForm={handleBookEvent}
            isLoading={bookIsLoading}
          />
        ) : (
          <GuestForm
            type="invite"
            eventName={eventTitle}
            currentGuests={guestNumber}
            allowedGuests={remainingGuests}
            handleSubmitForm={handleInviteToEvent}
            isLoading={inviteIsLoading}
          />
        )}
      </Modal>
    </div>
  );
};

export default Event;
